import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  MapIcon,
  CheckIcon,
  FireIcon,
  CircleStackIcon,
  PencilIcon,
  TrashIcon,
  FolderPlusIcon,
  Bars3Icon,
  ClipboardDocumentListIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { visualizationOptions as defaultVisualizationOptions } from '../map/mapConfig';
import { variablesSearchAPI, mapVisualizationOptionsAPI } from '../../services/api';
import VariableBrowser from './VariableBrowser';
import { autoSaveVariableAliases } from './VariableExportNamesEditor';

const STORAGE_KEY_HEAT = 'mapVisualizationOptions_heat';
const STORAGE_KEY_DOT = 'mapVisualizationOptions_dot';

// Default categories for map variables
const DEFAULT_CATEGORIES = [
  'Population & Households',
  'Daytime Population',
  'Age',
  'Age Detail',
  'Income',
  'Disposable Income',
  'Affluence & Affordability',
  'Housing',
  'Home Value Ranges',
  'Education',
  'Employment & Labor Force',
  'Employment by Race',
  'Unemployment by Race',
  'Economic Dependency',
  'Race & Ethnicity',
  'Projected Growth',
  'Historical Growth',
  'Future (2029)',
  'Density',
  'Other',
];

// Modal for adding variables with category selection
const AddVariableModal = ({ isOpen, onClose, onAdd, existingIds, existingCategories, title }) => {
  const [step, setStep] = useState(1); // 1 = select variables, 2 = select category
  const [localSelected, setLocalSelected] = useState(new Set());
  const [selectedCategory, setSelectedCategory] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [inputMode, setInputMode] = useState('browse'); // 'browse' or 'csv'
  const [csvInput, setCsvInput] = useState('');
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvValidated, setCsvValidated] = useState(false);
  const [csvVariables, setCsvVariables] = useState([]); // Validated variable details from CSV

  // Combine default categories with any custom ones from existing options
  const allCategories = useMemo(() => {
    const customCats = existingCategories.filter(cat => !DEFAULT_CATEGORIES.includes(cat));
    return [...DEFAULT_CATEGORIES, ...customCats];
  }, [existingCategories]);

  // Parse and validate CSV input
  const handleValidateCsv = async () => {
    if (!csvInput.trim()) {
      toast.error('Please paste variable IDs');
      return;
    }

    setCsvLoading(true);
    try {
      // Parse CSV - split by comma, newline, or both
      const ids = csvInput
        .split(/[,\n\r]+/)
        .map(id => id.trim())
        .filter(id => id.length > 0);

      console.log('CSV Validation - Parsed IDs:', ids);

      if (ids.length === 0) {
        toast.error('No valid variable IDs found');
        setCsvLoading(false);
        return;
      }

      // Filter out already existing IDs
      const newIds = ids.filter(id => !existingIds.has(id));
      const skippedCount = ids.length - newIds.length;

      if (newIds.length === 0) {
        toast.error('All variables are already added');
        setCsvLoading(false);
        return;
      }

      // Lookup variable details from API
      console.log('CSV Validation - Calling API with IDs:', newIds);
      const details = await variablesSearchAPI.getByIds(newIds);
      console.log('CSV Validation - API response:', details);
      const foundVariables = details.variables || [];

      // Track which IDs were found vs not found
      const foundIds = new Set(foundVariables.map(v => v.id));
      const notFoundIds = newIds.filter(id => !foundIds.has(id));

      if (foundVariables.length === 0) {
        toast.error('No valid variables found. Check the variable IDs.');
        setCsvLoading(false);
        return;
      }

      setCsvVariables(foundVariables);
      setCsvValidated(true);

      // Build success message
      let message = `Found ${foundVariables.length} variable(s)`;
      if (skippedCount > 0) {
        message += `, ${skippedCount} already added`;
      }
      if (notFoundIds.length > 0) {
        message += `, ${notFoundIds.length} not found`;
      }
      toast.success(message);

    } catch (error) {
      console.error('Failed to validate CSV:', error);
      // Show more specific error message
      if (error.response?.status === 401) {
        toast.error('Please log in to validate variables');
      } else if (error.response?.status === 503) {
        toast.error('Variables cache not available. Try again later.');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to validate variables. Check console for details.');
      }
    } finally {
      setCsvLoading(false);
    }
  };

  const handleNext = () => {
    if (inputMode === 'csv') {
      if (!csvValidated || csvVariables.length === 0) {
        toast.error('Please validate your CSV first');
        return;
      }
    } else {
      if (localSelected.size === 0) {
        toast.error('Please select at least one variable');
        return;
      }
    }
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleAdd = () => {
    let category = selectedCategory;

    if (showNewCategoryInput) {
      if (!newCategoryName.trim()) {
        toast.error('Please enter a category name');
        return;
      }
      category = newCategoryName.trim();
    } else if (!category) {
      toast.error('Please select a category');
      return;
    }

    // Use either CSV variables or browse-selected variables
    if (inputMode === 'csv') {
      const variableIds = csvVariables.map(v => v.id);
      onAdd(variableIds, category);
    } else {
      onAdd(Array.from(localSelected), category);
    }

    // Reset state
    setLocalSelected(new Set());
    setSelectedCategory('');
    setNewCategoryName('');
    setShowNewCategoryInput(false);
    setInputMode('browse');
    setCsvInput('');
    setCsvValidated(false);
    setCsvVariables([]);
    setStep(1);
    onClose();
  };

  const handleClose = () => {
    setLocalSelected(new Set());
    setSelectedCategory('');
    setNewCategoryName('');
    setShowNewCategoryInput(false);
    setInputMode('browse');
    setCsvInput('');
    setCsvValidated(false);
    setCsvVariables([]);
    setStep(1);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-3xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {title || 'Add Variables'}
            </h3>
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <span className={`px-2 py-0.5 rounded ${step === 1 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700'}`}>
                1. Select
              </span>
              <span className="text-gray-400">→</span>
              <span className={`px-2 py-0.5 rounded ${step === 2 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700'}`}>
                2. Category
              </span>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        {step === 1 ? (
          <>
            {/* Mode Toggle */}
            <div className="flex-shrink-0 px-4 pt-4 pb-2">
              <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 p-1 bg-gray-50 dark:bg-gray-700">
                <button
                  onClick={() => {
                    setInputMode('browse');
                    setCsvValidated(false);
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    inputMode === 'browse'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <ListBulletIcon className="h-4 w-4" />
                  Browse Variables
                </button>
                <button
                  onClick={() => {
                    setInputMode('csv');
                    setLocalSelected(new Set());
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    inputMode === 'csv'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <ClipboardDocumentListIcon className="h-4 w-4" />
                  Paste CSV
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 p-4">
              {inputMode === 'browse' ? (
                <VariableBrowser
                  selectedVariables={localSelected}
                  disabledVariables={existingIds}
                  onVariableSelect={(variable) => {
                    // Don't allow selecting already-saved variables
                    if (existingIds.has(variable.id)) {
                      return;
                    }
                    setLocalSelected(prev => {
                      const newSet = new Set(prev);
                      if (newSet.has(variable.id)) {
                        newSet.delete(variable.id);
                      } else {
                        newSet.add(variable.id);
                      }
                      return newSet;
                    });
                  }}
                />
              ) : (
                <div className="space-y-4 h-full flex flex-col">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Paste variable IDs from Esri's Variable Finder (comma or newline separated):
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mb-3">
                      Example: LifeModeGroups.THHLM01,LifeModeGroups.THHLM02,LifeStageGroups.THHLS01
                    </p>
                  </div>
                  <textarea
                    value={csvInput}
                    onChange={(e) => {
                      setCsvInput(e.target.value);
                      setCsvValidated(false);
                      setCsvVariables([]);
                    }}
                    placeholder="Paste variable IDs here..."
                    className="flex-1 w-full rounded-md border border-gray-300 dark:border-gray-600
                             px-3 py-2 text-sm font-mono focus:border-blue-500 focus:ring-blue-500
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  />
                  <div className="flex items-center justify-between">
                    <button
                      onClick={handleValidateCsv}
                      disabled={csvLoading || !csvInput.trim()}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-md
                               hover:bg-green-700 disabled:opacity-50"
                    >
                      {csvLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Validating...
                        </>
                      ) : (
                        <>
                          <CheckIcon className="h-4 w-4" />
                          Validate Variables
                        </>
                      )}
                    </button>
                    {csvValidated && (
                      <span className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                        <CheckIcon className="h-4 w-4" />
                        {csvVariables.length} variable{csvVariables.length !== 1 ? 's' : ''} ready
                      </span>
                    )}
                  </div>
                  {/* Show validated variables preview */}
                  {csvValidated && csvVariables.length > 0 && (
                    <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600">
                        Validated Variables ({csvVariables.length})
                      </div>
                      <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                        {csvVariables.map((v) => (
                          <div key={v.id} className="px-3 py-2 text-sm">
                            <div className="font-medium text-gray-900 dark:text-white truncate">
                              {v.alias || v.id}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {v.id}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {inputMode === 'csv'
                  ? (csvValidated ? `${csvVariables.length} validated` : 'Paste and validate variables')
                  : `${localSelected.size} selected`
                }
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600
                           rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNext}
                  disabled={inputMode === 'csv' ? !csvValidated || csvVariables.length === 0 : localSelected.size === 0}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md
                           hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  Next: Choose Category
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 min-h-0 p-4 overflow-y-auto">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Choose a category for the {inputMode === 'csv' ? csvVariables.length : localSelected.size} selected variable{(inputMode === 'csv' ? csvVariables.length : localSelected.size) !== 1 ? 's' : ''}:
                  </p>
                </div>

                {/* Category selection */}
                <div className="grid grid-cols-2 gap-2 max-h-[40vh] overflow-y-auto">
                  {allCategories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => {
                        setSelectedCategory(cat);
                        setShowNewCategoryInput(false);
                      }}
                      className={`p-3 text-left text-sm rounded-lg border transition-colors ${
                        selectedCategory === cat && !showNewCategoryInput
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                {/* Add new category option */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <button
                    onClick={() => {
                      setShowNewCategoryInput(!showNewCategoryInput);
                      setSelectedCategory('');
                    }}
                    className={`w-full p-3 text-left text-sm rounded-lg border transition-colors flex items-center gap-2 ${
                      showNewCategoryInput
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'border-dashed border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Create New Category
                  </button>

                  {showNewCategoryInput && (
                    <div className="mt-3">
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="Enter new category name..."
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600
                                 px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleBack}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600
                         rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <ChevronDownIcon className="h-4 w-4 rotate-90" />
                Back
              </button>
              <button
                onClick={handleAdd}
                disabled={!selectedCategory && !newCategoryName.trim()}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-md
                         hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Add to {showNewCategoryInput ? (newCategoryName || 'Category') : (selectedCategory || 'Category')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Modal for editing category name
const EditCategoryModal = ({ isOpen, onClose, categoryName, onSave }) => {
  const [newName, setNewName] = useState(categoryName || '');

  useEffect(() => {
    setNewName(categoryName || '');
  }, [categoryName, isOpen]);

  const handleSave = () => {
    if (!newName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    if (newName.trim() === categoryName) {
      onClose();
      return;
    }
    onSave(newName.trim());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {categoryName ? 'Rename Category' : 'Create New Category'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Category Name
          </label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Enter category name..."
            className="w-full rounded-md border border-gray-300 dark:border-gray-600
                     px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') onClose();
            }}
          />
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600
                     rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!newName.trim()}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md
                     hover:bg-blue-700 disabled:opacity-50"
          >
            {categoryName ? 'Rename' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal for confirming category deletion
const DeleteCategoryModal = ({ isOpen, onClose, categoryName, variableCount, onDelete }) => {
  const [deleteVariables, setDeleteVariables] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Delete Category
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to delete the category <strong>"{categoryName}"</strong>?
          </p>
          {variableCount > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
              <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-3">
                This category contains <strong>{variableCount}</strong> variable{variableCount > 1 ? 's' : ''}.
              </p>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="deleteOption"
                    checked={!deleteVariables}
                    onChange={() => setDeleteVariables(false)}
                    className="text-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Move variables to "Other" category
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="deleteOption"
                    checked={deleteVariables}
                    onChange={() => setDeleteVariables(true)}
                    className="text-red-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Delete all variables in this category
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600
                     rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onDelete(deleteVariables);
              onClose();
            }}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete Category
          </button>
        </div>
      </div>
    </div>
  );
};

// Category item component
const CategorySection = ({
  categoryName,
  variables,
  isExpanded,
  onToggle,
  onRemoveVariable,
  variableDetails,
  onEditCategory,
  onDeleteCategory,
  dragHandleProps,
  isDragging,
}) => {
  return (
    <div className={`border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ${isDragging ? 'opacity-50 shadow-lg' : ''}`}>
      <div
        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {/* Drag handle */}
          <div
            {...dragHandleProps}
            className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            onClick={(e) => e.stopPropagation()}
            title="Drag to reorder"
          >
            <Bars3Icon className="h-4 w-4" />
          </div>
          {isExpanded ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-500" />
          )}
          <span className="font-medium text-gray-900 dark:text-white">
            {categoryName}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ({variables.length} variable{variables.length !== 1 ? 's' : ''})
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEditCategory(categoryName);
            }}
            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
            title="Rename category"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteCategory(categoryName, variables.length);
            }}
            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
            title="Delete category"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
          {variables.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
              No variables in this category.
            </div>
          ) : (
            variables.map((variable) => {
              const details = variableDetails[variable.value];
              return (
                <div
                  key={variable.value}
                  className="flex items-center justify-between p-2 pl-8 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {variable.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      ID: {variable.value}
                      {details?.fieldCategory && ` | ${details.fieldCategory}`}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveVariable(variable.value);
                    }}
                    className="ml-2 p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                    title="Remove from map variables"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// Sortable wrapper for CategorySection
const SortableCategorySection = ({
  id,
  categoryName,
  variables,
  isExpanded,
  onToggle,
  onRemoveVariable,
  variableDetails,
  onEditCategory,
  onDeleteCategory,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <CategorySection
        categoryName={categoryName}
        variables={variables}
        isExpanded={isExpanded}
        onToggle={onToggle}
        onRemoveVariable={onRemoveVariable}
        variableDetails={variableDetails}
        onEditCategory={onEditCategory}
        onDeleteCategory={onDeleteCategory}
        dragHandleProps={listeners}
        isDragging={isDragging}
      />
    </div>
  );
};

// Variables list panel component
const VariablesPanel = ({
  options,
  setOptions,
  storageKey,
  defaultOptions,
  title,
  description,
  icon: Icon,
  iconColor,
}) => {
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [variableDetails, setVariableDetails] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Category management state
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [deletingCategoryCount, setDeletingCategoryCount] = useState(0);
  const [emptyCategories, setEmptyCategories] = useState(new Set()); // Track empty categories
  const [categoryOrder, setCategoryOrder] = useState([]); // Custom category order

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load variable details for display enhancement
  useEffect(() => {
    const loadDetails = async () => {
      if (options.length === 0) return;

      const ids = options.map(o => o.value);
      try {
        const details = await variablesSearchAPI.getByIds(ids);
        const detailsMap = {};
        (details.variables || []).forEach(v => {
          detailsMap[v.id] = v;
        });
        setVariableDetails(detailsMap);
      } catch (error) {
        console.log('Could not load variable details:', error);
      }
    };

    loadDetails();
  }, [options]);

  // Load category order from localStorage
  useEffect(() => {
    const orderKey = `${storageKey}_categoryOrder`;
    const storedOrder = localStorage.getItem(orderKey);
    if (storedOrder) {
      try {
        setCategoryOrder(JSON.parse(storedOrder));
      } catch {
        setCategoryOrder([]);
      }
    }
  }, [storageKey]);

  // Group options by category
  const groupedOptions = useMemo(() => {
    const groups = {};
    const filtered = searchQuery
      ? options.filter(opt =>
          opt.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          opt.value.toLowerCase().includes(searchQuery.toLowerCase()) ||
          opt.category?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : options;

    filtered.forEach(opt => {
      const cat = opt.category || 'Other';
      if (!groups[cat]) {
        groups[cat] = [];
      }
      groups[cat].push(opt);
    });

    // Include empty categories (only if not searching)
    if (!searchQuery) {
      emptyCategories.forEach(cat => {
        if (!groups[cat]) {
          groups[cat] = [];
        }
      });
    }

    return groups;
  }, [options, searchQuery, emptyCategories]);

  // Get sorted category names based on custom order
  const sortedCategoryNames = useMemo(() => {
    const categoryNames = Object.keys(groupedOptions);

    // Sort based on custom order, with unordered categories at the end (alphabetically)
    return categoryNames.sort((a, b) => {
      const indexA = categoryOrder.indexOf(a);
      const indexB = categoryOrder.indexOf(b);

      // Both are in custom order
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // Only a is in custom order
      if (indexA !== -1) {
        return -1;
      }
      // Only b is in custom order
      if (indexB !== -1) {
        return 1;
      }
      // Neither is in custom order, sort alphabetically
      return a.localeCompare(b);
    });
  }, [groupedOptions, categoryOrder]);

  // Handle drag end for category reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedCategoryNames.indexOf(active.id);
      const newIndex = sortedCategoryNames.indexOf(over.id);

      const newOrder = arrayMove(sortedCategoryNames, oldIndex, newIndex);
      setCategoryOrder(newOrder);

      // Save to localStorage
      const orderKey = `${storageKey}_categoryOrder`;
      localStorage.setItem(orderKey, JSON.stringify(newOrder));

      setHasChanges(true);
      toast.success('Category order updated');
    }
  };

  const existingIds = useMemo(() => {
    return new Set(options.map(o => o.value));
  }, [options]);

  // Get existing categories from current options (including empty ones)
  const existingCategories = useMemo(() => {
    const cats = new Set([
      ...options.map(o => o.category).filter(Boolean),
      ...emptyCategories
    ]);
    return Array.from(cats);
  }, [options, emptyCategories]);

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const handleRemoveVariable = (variableId) => {
    const newOptions = options.filter(o => o.value !== variableId);
    setOptions(newOptions);
    setHasChanges(true);
  };

  const handleAddVariables = async (variableIds, category) => {
    try {
      const details = await variablesSearchAPI.getByIds(variableIds);
      const foundVariables = details.variables || [];

      // Auto-save variable aliases for export (so they appear with friendly names in Excel)
      autoSaveVariableAliases(foundVariables);

      const newVars = foundVariables.map(v => ({
        value: v.id,
        label: v.alias || v.id,
        category: category, // Use the selected category
      }));

      const newOptions = [...options, ...newVars.filter(nv => !existingIds.has(nv.value))];
      setOptions(newOptions);
      setHasChanges(true);

      // Remove from empty categories if it was one
      if (emptyCategories.has(category)) {
        setEmptyCategories(prev => {
          const newSet = new Set(prev);
          newSet.delete(category);
          return newSet;
        });
      }

      toast.success(`Added ${newVars.length} variable(s) to "${category}"`);
    } catch (error) {
      console.error('Failed to add variables:', error);
      toast.error('Failed to add variables');
    }
  };

  const handleResetToDefaults = () => {
    if (window.confirm(`Reset ${title} to defaults? This will discard your customizations.`)) {
      setOptions([...defaultOptions]);
      setEmptyCategories(new Set()); // Clear empty categories
      setCategoryOrder([]); // Reset category order
      const orderKey = `${storageKey}_categoryOrder`;
      localStorage.removeItem(orderKey);
      setHasChanges(true);
      toast.success('Reset to defaults');
    }
  };

  const handleSave = async () => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(options));
      toast.success(`${title} saved`);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save options:', error);
      toast.error('Failed to save');
    }
  };

  // Category management handlers
  const handleEditCategory = (categoryName) => {
    setEditingCategory(categoryName);
    setShowEditCategoryModal(true);
  };

  const handleRenameCategory = (newName) => {
    if (!editingCategory) return;

    // Check if new name already exists
    if (existingCategories.includes(newName) && newName !== editingCategory) {
      toast.error(`Category "${newName}" already exists`);
      return;
    }

    // Update category order if this category is in the order
    if (categoryOrder.includes(editingCategory)) {
      const newOrder = categoryOrder.map(cat => cat === editingCategory ? newName : cat);
      setCategoryOrder(newOrder);
      const orderKey = `${storageKey}_categoryOrder`;
      localStorage.setItem(orderKey, JSON.stringify(newOrder));
    }

    // Check if it's an empty category
    if (emptyCategories.has(editingCategory)) {
      setEmptyCategories(prev => {
        const newSet = new Set(prev);
        newSet.delete(editingCategory);
        newSet.add(newName);
        return newSet;
      });
      setHasChanges(true);
      setEditingCategory(null);
      toast.success(`Category renamed to "${newName}"`);
      return;
    }

    // Update all variables in this category
    const newOptions = options.map(opt => {
      if (opt.category === editingCategory) {
        return { ...opt, category: newName };
      }
      return opt;
    });

    setOptions(newOptions);
    setHasChanges(true);
    setEditingCategory(null);
    toast.success(`Category renamed to "${newName}"`);
  };

  const handleDeleteCategoryClick = (categoryName, variableCount) => {
    setDeletingCategory(categoryName);
    setDeletingCategoryCount(variableCount);
    setShowDeleteCategoryModal(true);
  };

  const handleDeleteCategory = (deleteVariables) => {
    if (!deletingCategory) return;

    // Remove from category order if present
    if (categoryOrder.includes(deletingCategory)) {
      const newOrder = categoryOrder.filter(cat => cat !== deletingCategory);
      setCategoryOrder(newOrder);
      const orderKey = `${storageKey}_categoryOrder`;
      localStorage.setItem(orderKey, JSON.stringify(newOrder));
    }

    // Check if it's an empty category
    if (emptyCategories.has(deletingCategory)) {
      setEmptyCategories(prev => {
        const newSet = new Set(prev);
        newSet.delete(deletingCategory);
        return newSet;
      });
      setHasChanges(true);
      toast.success(`Deleted category "${deletingCategory}"`);
      setDeletingCategory(null);
      setDeletingCategoryCount(0);
      return;
    }

    let newOptions;
    if (deleteVariables) {
      // Remove all variables in this category
      newOptions = options.filter(opt => opt.category !== deletingCategory);
      toast.success(`Deleted category "${deletingCategory}" and its ${deletingCategoryCount} variable(s)`);
    } else {
      // Move all variables to "Other" category
      newOptions = options.map(opt => {
        if (opt.category === deletingCategory) {
          return { ...opt, category: 'Other' };
        }
        return opt;
      });
      toast.success(`Deleted category "${deletingCategory}" and moved ${deletingCategoryCount} variable(s) to "Other"`);
    }

    setOptions(newOptions);
    setHasChanges(true);
    setDeletingCategory(null);
    setDeletingCategoryCount(0);
  };

  const handleCreateCategory = () => {
    setEditingCategory(null); // null indicates we're creating, not editing
    setShowEditCategoryModal(true);
  };

  const handleSaveNewCategory = (categoryName) => {
    // Check if category already exists
    if (existingCategories.includes(categoryName)) {
      toast.error(`Category "${categoryName}" already exists`);
      return;
    }

    // Add to empty categories set
    setEmptyCategories(prev => new Set([...prev, categoryName]));
    setHasChanges(true);
    toast.success(`Category "${categoryName}" created`);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <Icon className={`h-5 w-5 ${iconColor}`} />
            {title}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {description}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleResetToDefaults}
            className="flex items-center px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                     rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Reset to default variables"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Reset
          </button>
          <button
            onClick={handleCreateCategory}
            className="flex items-center px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                     rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Create a new category"
          >
            <FolderPlusIcon className="h-4 w-4 mr-2" />
            Add Category
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md
                     hover:bg-blue-700 text-sm"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Variables
          </button>
        </div>
      </div>

      {/* Save indicator */}
      {hasChanges && (
        <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/30
                      border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <span className="text-sm text-yellow-800 dark:text-yellow-200">
            You have unsaved changes
          </span>
          <button
            onClick={handleSave}
            className="flex items-center px-3 py-1.5 bg-yellow-600 text-white rounded-md
                     hover:bg-yellow-700 text-sm"
          >
            <CheckIcon className="h-4 w-4 mr-1" />
            Save Changes
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search variables..."
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 pl-10
                   shadow-sm px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500
                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="text-sm text-gray-600 dark:text-gray-400">
        {options.length} total variables across {Object.keys(groupedOptions).length} categories
        {searchQuery && ` (showing ${Object.values(groupedOptions).flat().length} matching)`}
      </div>

      {/* Categories */}
      {Object.keys(groupedOptions).length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Icon className={`h-12 w-12 mx-auto mb-3 opacity-50 ${iconColor}`} />
          <p>No variables configured.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-2 text-blue-600 hover:underline"
          >
            Add some variables
          </button>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedCategoryNames}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2 max-h-[calc(100vh-500px)] overflow-y-auto">
              {sortedCategoryNames.map((categoryName) => (
                <SortableCategorySection
                  key={categoryName}
                  id={categoryName}
                  categoryName={categoryName}
                  variables={groupedOptions[categoryName] || []}
                  isExpanded={expandedCategories.has(categoryName)}
                  onToggle={() => toggleCategory(categoryName)}
                  onRemoveVariable={handleRemoveVariable}
                  variableDetails={variableDetails}
                  onEditCategory={handleEditCategory}
                  onDeleteCategory={handleDeleteCategoryClick}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add Variable Modal */}
      <AddVariableModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddVariables}
        existingIds={existingIds}
        existingCategories={existingCategories}
        title={`Add ${title}`}
      />

      {/* Edit Category Modal */}
      <EditCategoryModal
        isOpen={showEditCategoryModal}
        onClose={() => {
          setShowEditCategoryModal(false);
          setEditingCategory(null);
        }}
        categoryName={editingCategory}
        onSave={editingCategory ? handleRenameCategory : handleSaveNewCategory}
      />

      {/* Delete Category Modal */}
      <DeleteCategoryModal
        isOpen={showDeleteCategoryModal}
        onClose={() => {
          setShowDeleteCategoryModal(false);
          setDeletingCategory(null);
          setDeletingCategoryCount(0);
        }}
        categoryName={deletingCategory}
        variableCount={deletingCategoryCount}
        onDelete={handleDeleteCategory}
      />
    </div>
  );
};

const MapVariablesEditor = () => {
  const [heatOptions, setHeatOptions] = useState([]);
  const [dotOptions, setDotOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load options on mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoading(true);

        // Try to load from API first
        try {
          const response = await mapVisualizationOptionsAPI.get();
          if (response.data) {
            if (response.data.heatOptions && response.data.heatOptions.length > 0) {
              setHeatOptions(response.data.heatOptions);
            } else {
              // Fall back to localStorage or defaults
              const storedHeat = localStorage.getItem(STORAGE_KEY_HEAT);
              if (storedHeat) {
                setHeatOptions(JSON.parse(storedHeat));
              } else {
                setHeatOptions([...defaultVisualizationOptions]);
              }
            }

            if (response.data.dotOptions && response.data.dotOptions.length > 0) {
              setDotOptions(response.data.dotOptions);
            } else {
              // Fall back to localStorage or defaults
              const storedDot = localStorage.getItem(STORAGE_KEY_DOT);
              if (storedDot) {
                setDotOptions(JSON.parse(storedDot));
              } else {
                setDotOptions([...defaultVisualizationOptions]);
              }
            }
            return;
          }
        } catch {
          console.log('API not available, using localStorage/defaults');
        }

        // Fall back to localStorage
        const storedHeat = localStorage.getItem(STORAGE_KEY_HEAT);
        const storedDot = localStorage.getItem(STORAGE_KEY_DOT);

        if (storedHeat) {
          try {
            setHeatOptions(JSON.parse(storedHeat));
          } catch {
            setHeatOptions([...defaultVisualizationOptions]);
          }
        } else {
          setHeatOptions([...defaultVisualizationOptions]);
        }

        if (storedDot) {
          try {
            setDotOptions(JSON.parse(storedDot));
          } catch {
            setDotOptions([...defaultVisualizationOptions]);
          }
        } else {
          setDotOptions([...defaultVisualizationOptions]);
        }
      } catch (error) {
        console.error('Failed to load options:', error);
        setHeatOptions([...defaultVisualizationOptions]);
        setDotOptions([...defaultVisualizationOptions]);
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Header */}
      <div className="flex items-center gap-2">
        <MapIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Map Visualization Variables
        </h2>
      </div>

      {/* Two Independent Panels */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Heat Map Variables Panel */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
          <VariablesPanel
            options={heatOptions}
            setOptions={setHeatOptions}
            storageKey={STORAGE_KEY_HEAT}
            defaultOptions={defaultVisualizationOptions}
            title="Heat Map Variables"
            description="Variables for choropleth/heat map visualizations. Best for rates, averages, and density metrics."
            icon={FireIcon}
            iconColor="text-orange-500"
          />
        </div>

        {/* Dot Density Variables Panel */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
          <VariablesPanel
            options={dotOptions}
            setOptions={setDotOptions}
            storageKey={STORAGE_KEY_DOT}
            defaultOptions={defaultVisualizationOptions}
            title="Dot Density Variables"
            description="Variables for dot density visualizations. Best for counts and absolute numbers."
            icon={CircleStackIcon}
            iconColor="text-blue-500"
          />
        </div>
      </div>
    </div>
  );
};

export default MapVariablesEditor;
